import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

type Model = 'build' | 'scale';

interface CompareRow {
  label: string;
  buildValue: string;
  buildDetail: string;
  scaleValue: string;
  scaleDetail: string;
  icon: string;
}

const rows: CompareRow[] = [
  {
    label: 'Timeline',
    buildValue: '4 weeks – 12 months',
    buildDetail: 'Fixed sprints with milestone gates',
    scaleValue: '48 hours – 2 weeks',
    scaleDetail: 'From brief to onboarded engineer',
    icon: 'ri-time-line',
  },
  {
    label: 'Team Size',
    buildValue: '2 – 15 engineers',
    buildDetail: 'Scales up or down per sprint',
    scaleValue: '1 – 20+ engineers',
    scaleDetail: 'Add capacity on demand',
    icon: 'ri-group-line',
  },
  {
    label: 'Pricing Model',
    buildValue: 'Fixed Scope or T&M',
    buildDetail: 'Milestone-based billing',
    scaleValue: 'Monthly or Hourly Rate',
    scaleDetail: 'Transparent, no markups',
    icon: 'ri-coins-line',
  },
  {
    label: 'Process',
    buildValue: 'Discovery → Design → Sprints → Launch',
    buildDetail: 'Full product lifecycle',
    scaleValue: 'Vet → Match → Interview → Onboard',
    buildDetail: '4-stage screening, 12% pass rate',
    icon: 'ri-route-line',
  },
  {
    label: 'Deliverables',
    buildValue: 'Shipped product + source code',
    buildDetail: 'You own all IP on delivery',
    scaleValue: 'Production-ready engineer',
    scaleDetail: 'Coding test + live interview included',
    icon: 'ri-stack-line',
  },
  {
    label: 'Ideal For',
    buildValue: 'New products, platforms, AI builds',
    buildDetail: 'When you need output, not just heads',
    scaleValue: 'Team gaps, surge capacity, niche skills',
    scaleDetail: 'When you need talent fast without hiring overhead',
    icon: 'ri-lightbulb-line',
  },
  {
    label: 'Risk Profile',
    buildValue: 'Low — fixed scope, milestone gates',
    buildDetail: 'Change requests with impact assessment',
    scaleValue: 'Minimal — 14-day replacement',
    scaleDetail: 'No long-term contracts required',
    icon: 'ri-shield-check-line',
  },
];

export default function CompareModelsSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [activeModel, setActiveModel] = useState<Model>('build');

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-arrow-left-right-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Compare Models</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Build Products or
            <span className="text-primary-600"> Scale Teams?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Toggle between our two delivery models to see which fits your current challenge.
          </motion.p>
        </motion.div>

        {/* Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="flex items-center justify-center mb-10 md:mb-14"
        >
          <div className="inline-flex items-center bg-white border border-gray-200 rounded-full p-1.5 shadow-sm">
            <button
              onClick={() => setActiveModel('build')}
              className={`relative px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeModel === 'build'
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-navy-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <i className="ri-code-box-line"></i>
                Build Products
              </span>
            </button>
            <button
              onClick={() => setActiveModel('scale')}
              className={`relative px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeModel === 'scale'
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-navy-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <i className="ri-user-add-line"></i>
                Scale Teams
              </span>
            </button>
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, ...springTransition }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
        >
          {/* Desktop header */}
          <div className="hidden md:grid grid-cols-[1fr_1.2fr_1.2fr] border-b border-gray-100">
            <div className="p-5 bg-gray-50/50"></div>
            <div className="p-5 bg-primary-50/50 border-l border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                  <i className="ri-code-box-line text-white text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-navy-900">Build Products</div>
                  <div className="text-xs text-gray-500">Software Development</div>
                </div>
              </div>
            </div>
            <div className="p-5 bg-amber-50/50 border-l border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500 rounded-lg">
                  <i className="ri-user-add-line text-white text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-navy-900">Scale Teams</div>
                  <div className="text-xs text-gray-500">Talent Solutions</div>
                </div>
              </div>
            </div>
          </div>

          {/* Rows */}
          {rows.map((row, i) => (
            <motion.div
              key={row.label}
              initial={{ opacity: 0, x: -10 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.45 + i * 0.04, ...springTransition }}
              className={`grid grid-cols-1 md:grid-cols-[1fr_1.2fr_1.2fr] border-b border-gray-100 last:border-b-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
            >
              {/* Label */}
              <div className="p-4 md:p-5 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                  <i className={`${row.icon} text-gray-500 text-sm`}></i>
                </div>
                <span className="text-sm font-bold text-navy-900">{row.label}</span>
              </div>

              {/* Build cell */}
              <div className={`p-4 md:p-5 border-l-0 md:border-l border-gray-100 relative ${activeModel === 'build' ? 'bg-primary-50/30' : ''}`}>
                <div className="flex items-center gap-2 md:hidden mb-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-primary-500 rounded-md">
                    <i className="ri-code-box-line text-white text-[10px]"></i>
                  </div>
                  <span className="text-xs font-bold text-primary-600">Build</span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`build-${activeModel}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="text-sm font-semibold text-navy-900">{row.buildValue}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{row.buildDetail}</div>
                  </motion.div>
                </AnimatePresence>
                {activeModel === 'build' && (
                  <motion.div
                    layoutId="highlight-bar"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 hidden md:block"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </div>

              {/* Scale cell */}
              <div className={`p-4 md:p-5 border-l-0 md:border-l border-gray-100 relative ${activeModel === 'scale' ? 'bg-amber-50/30' : ''}`}>
                <div className="flex items-center gap-2 md:hidden mb-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-md">
                    <i className="ri-user-add-line text-white text-[10px]"></i>
                  </div>
                  <span className="text-xs font-bold text-amber-600">Scale</span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`scale-${activeModel}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="text-sm font-semibold text-navy-900">{row.scaleValue}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{row.scaleDetail}</div>
                  </motion.div>
                </AnimatePresence>
                {activeModel === 'scale' && (
                  <motion.div
                    layoutId="highlight-bar"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 hidden md:block"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, ...springTransition }}
          className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
        >
          <Link
            to="/software-development"
            className="group flex items-center justify-between bg-white border border-gray-100 rounded-2xl p-5 md:p-6 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                <i className="ri-code-box-line text-white"></i>
              </div>
              <div>
                <div className="text-sm font-bold text-navy-900">Start Building</div>
                <div className="text-xs text-gray-500">Get a free project roadmap</div>
              </div>
            </div>
            <i className="ri-arrow-right-line text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
          </Link>

          <Link
            to="/talent-solutions"
            className="group flex items-center justify-between bg-white border border-gray-100 rounded-2xl p-5 md:p-6 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl">
                <i className="ri-user-add-line text-white"></i>
              </div>
              <div>
                <div className="text-sm font-bold text-navy-900">Start Scaling</div>
                <div className="text-xs text-gray-500">Request talent matches in 48h</div>
              </div>
            </div>
            <i className="ri-arrow-right-line text-gray-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}